import React from 'react';
import { RenderRequest, RenderResponse, DynamicRenderResponse } from 'types/cx-editor';
import { useGlobalConfig } from 'context/GlobalContext';
import { createCxApiClient } from 'framework/http/cxEditorClient';
import { useDebounceEffect } from 'hooks/useDebounceEffect';
import { PreviewFrame } from './PreviewFrame';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { selectProjectAttributes } from 'store/dataReducers/applicationScopeSlice';
import { parseHtmlSections, SECTION_ID_TO_ELEMENT_KEY_MAP } from 'utils/html-section-parser';
import { resolveScenarioId } from 'utils/cx-editor';
import { isWebWidgetTemplate } from 'constants/web-widget-mapping';

type Props = { 
  request: RenderRequest | null; 
  rightRequest?: RenderRequest | null;
  previewMode?: 'desktop' | 'mobile';
  channel?: string;
  isFullTemplateSelected?: boolean;
  projectId?: string | null;
  elementKey?: string; // Current selected element/section
  onSectionsParsed?: (sections: Array<{ elementKey: string; displayName: string }>) => void; // Callback to update element dropdown
  onRenderComplete?: (templateKey: string) => void; // Callback when render completes to trigger template/scenario fetch
  onHtmlChange?: (html: string) => void; // Callback to expose rendered HTML to parent
  onLoadingChange?: (loading: boolean) => void; // Callback to indicate preview is busy (used to disable UI actions)
  variant?: { scenario?: string } | null; // Variant with scenario for Web widgets
  onWebWidgetFinished?: (templateKey: string, result: { success: boolean; error?: string }) => void; // Web widget render completion
};

export const PreviewArea: React.FC<Props> = ({ 
  request, 
  rightRequest,
  previewMode = 'desktop',
  channel = 'Email',
  isFullTemplateSelected = false,
  projectId,
  elementKey = 'Full Template',
  onSectionsParsed,
  onRenderComplete,
  onHtmlChange,
  onLoadingChange,
  variant,
  onWebWidgetFinished,
}) => {
  const { rc } = useGlobalConfig();
  const selectedProjectId = useSelector(selectSelectedPilotId);
  const projectAttributes = useSelector(selectProjectAttributes);
  const effectiveProjectId = projectId || selectedProjectId;
  
  // Extract fuel_type from projectAttributes (similar to InspectorPanel)
  const fuelType = React.useMemo(() => {
    let fuelType = 'ELECTRIC'; // Default fallback
    if (projectAttributes?.fuel_type) {
      // Handle different formats: string, array, or comma-separated
      if (typeof projectAttributes.fuel_type === 'string') {
        // If comma-separated, take the first one
        const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
        fuelType = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
      } else if (Array.isArray(projectAttributes.fuel_type)) {
        const fuelTypeArray = projectAttributes.fuel_type as any[];
        if (fuelTypeArray.length > 0) {
          fuelType = String(fuelTypeArray[0]).toUpperCase();
        }
      }
    }
    return fuelType;
  }, [projectAttributes]);
  
  const [fullHtml, setFullHtml] = React.useState<string>(''); // Store full rendered HTML
  const [html, setHtml] = React.useState<string>(''); // Filtered HTML based on elementKey
  const [loading, setLoading] = React.useState(false);
  const [subject, setSubject] = React.useState<string>('');
  const [htmlB, setHtmlB] = React.useState<string>('');
  const [loadingB, setLoadingB] = React.useState(false);
  const [subjectB, setSubjectB] = React.useState<string>('');
  const abortRef = React.useRef<AbortController | null>(null);
  const abortRefB = React.useRef<AbortController | null>(null);
  
  // Helper function to get sectionNames array from elementKey
  // Returns empty array for Full Template, or array with technical section name
  // This function works without HTML by using SECTION_ID_TO_ELEMENT_KEY_MAP for reverse lookup
  const getSectionNames = React.useCallback((elementKey: string): string[] => {
    if (elementKey === 'Full Template') {
      return [];
    }
    
    // Check if elementKey is already a technical section ID (exists as key in SECTION_ID_TO_ELEMENT_KEY_MAP)
    // This handles cases where EditorToolbar passes technical section IDs directly (e.g., "MONTHLY_SUMMARY_V2")
    if (SECTION_ID_TO_ELEMENT_KEY_MAP[elementKey]) {
      return [elementKey];
    }
    
    // Reverse lookup: Find all technical section IDs that map to this display name
    // Prefer higher version numbers (e.g., V4 > V3 > V2)
    const matchingTechnicalIds: string[] = [];
    for (const [technicalId, displayName] of Object.entries(SECTION_ID_TO_ELEMENT_KEY_MAP)) {
      if (displayName === elementKey) {
        matchingTechnicalIds.push(technicalId);
      }
    }
    
    if (matchingTechnicalIds.length > 0) {
      // Sort by version number (higher first) and return the first one
      matchingTechnicalIds.sort((a, b) => {
        const versionA = parseInt(a.match(/_[Vv](\d+)/)?.[1] || '0');
        const versionB = parseInt(b.match(/_[Vv](\d+)/)?.[1] || '0');
        return versionB - versionA; // Higher version first
      });
      return [matchingTechnicalIds[0]];
    }
    
    // Final fallback: if elementKey might be a technical section ID that's not in mapping,
    // or we can't determine the technical section ID, return empty array (backend will handle full template)
    // This is safe because backend can handle empty array for full template
    return [];
  }, []);
  
  // Client-side filtering removed - API will handle filtering based on sectionNames

  // Extract primitive values from request to use as stable dependencies
  const templateKey = request?.templateKey || null;
  const scenario = request?.variant?.scenario || null;
  const locale = request?.variant?.locale || 'en_US';
  const refreshKey = request?.refreshKey ?? null;

  const isWebWidget = channel === 'Web' && isWebWidgetTemplate(templateKey);

  // Surface a single "busy" signal to parent (used to disable section/scenario/template changes).
  React.useEffect(() => {
    onLoadingChange?.(loading || loadingB);
  }, [loading, loadingB, onLoadingChange]);
  
  // API call when template, scenario, refreshKey, or elementKey changes
  // elementKey changes trigger new API call with updated sectionNames
  useDebounceEffect(() => {
    // For Web widget templates, skip CX render API – widgets SDK will handle rendering
    if (isWebWidget) {
      abortRef.current?.abort();
      setFullHtml('');
      setHtml('');
      setSubject('');
      setLoading(false);
      return;
    }

    if (!request || !effectiveProjectId || !templateKey || !scenario) {
      abortRef.current?.abort();
      setFullHtml(''); 
      setHtml('');
      setSubject('');
      setLoading(false);
      return;
    }

    // Cancel any previous in-flight request before starting a new one.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    let active = true;
    (async () => {
      setLoading(true);
      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        
        // Resolve "default" scenario to actual scenario ID from data scenarios API
        const resolvedScenarioId = await resolveScenarioId(
          scenario || 'default',
          templateKey || '',
          channel || 'Email',
          effectiveProjectId || '',
          fuelType,
          locale,
          (pilot_id, fuel_type, locale, signal) => api.getDataScenarios(pilot_id, fuel_type, locale, { signal }),
          controller.signal
        );
        
        // Note: resolvedScenarioId is the actual scenario ID (e.g., "MS_1", "MS_2"), not "default"
        // Backend /v1/cx/render expects: { projectId, scenarioId, uuid?, sectionNames? }
        // The scenarioId should be resolved and contain template information
        // sectionNames: empty array for Full Template, or array with technical section name
        // Get sectionNames based on current elementKey (don't use fullHtml for lookup since we're calling API)
        const sectionNamesForApi = getSectionNames(elementKey);
        console.log('[PreviewArea] Calling renderDynamic API', {
          projectId: effectiveProjectId,
          originalScenario: scenario,
          resolvedScenarioId,
          templateKey: templateKey,
          elementKey,
          sectionNames: sectionNamesForApi,
          apiUrl: process.env.REACT_APP_TOOL_API_URL
        });
        
        const resp: DynamicRenderResponse = await api.renderDynamic({
          projectId: effectiveProjectId || '',
          scenarioId: resolvedScenarioId,
          uuid: '', // UUID is optional, backend will get it from project
          sectionNames: sectionNamesForApi
        }, { signal: controller.signal });
        console.log('[PreviewArea] API response received', { 
          hasHtml: !!resp.html,
          htmlLength: resp.html?.length
        });
        if (active && !controller.signal.aborted) {
          const renderedHtml = resp.html || '';
          // API returns filtered HTML based on sectionNames, so use it directly (no client-side filtering)
          setFullHtml(renderedHtml); // Store full HTML (may be filtered by backend)
          setHtml(renderedHtml); // Use API response directly - backend handles filtering
          setSubject(resp.subject || ''); // renderDynamic returns subject
          
          // Expose HTML to parent (for InspectorPanel to get technical section IDs)
          // Note: For section-specific renders, HTML may be filtered, but we still expose it
          if (onHtmlChange && renderedHtml) {
            onHtmlChange(renderedHtml);
          }
          
          // Parse sections from HTML and notify parent
          // Only parse when rendering Full Template to get all available sections
          if (renderedHtml && onSectionsParsed && elementKey === 'Full Template') {
            const { elementKeys } = parseHtmlSections(renderedHtml);
            // Create elements array with "Full Template" as first option
            const sections = [
              { elementKey: 'Full Template', displayName: 'Full Template' },
              ...elementKeys.map(key => ({ elementKey: key, displayName: key }))
            ];
            onSectionsParsed(sections);
          }
          
          // Notify parent that render is complete - trigger template/scenario fetch
          if (onRenderComplete && templateKey) {
            onRenderComplete(templateKey);
          }
        }
      } catch (err: any) {
        const isAborted =
          err?.code === 'ERR_CANCELED' ||
          err?.name === 'CanceledError' ||
          controller.signal.aborted === true;
        if (active && !isAborted) {
          console.error('[PreviewArea] API call failed', err);
          const errorHtml = '<div style="padding:12px;color:crimson">Failed to render preview</div>';
          setFullHtml(errorHtml);
          setHtml(errorHtml);
          setSubject('');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();
    return () => { 
      active = false;
      controller.abort();
    };
  }, 400, [templateKey, scenario, locale, effectiveProjectId, elementKey, refreshKey, isWebWidget, fuelType, channel]); // include locale/channel/fuelType to keep scenario resolution stable

  // Extract primitive values from rightRequest for stable dependencies
  const rightTemplateKey = rightRequest?.templateKey || null;
  const rightScenario = rightRequest?.variant?.scenario || null;
  const rightLocale = rightRequest?.variant?.locale || null;
  
  useDebounceEffect(() => {
    let active = true;
    (async () => {
      // For Web widget templates, skip CX render API – widgets SDK will handle rendering
      if (channel === 'Web' && isWebWidgetTemplate(rightTemplateKey)) {
        abortRefB.current?.abort();
        setHtmlB('');
        setSubjectB('');
        setLoadingB(false);
        return;
      }

      if (!rightRequest || !effectiveProjectId || !rightTemplateKey || !rightScenario) { 
        abortRefB.current?.abort();
        setHtmlB(''); 
        setSubjectB('');
        setLoadingB(false);
        return; 
      }
      
      abortRefB.current?.abort();
      const controller = new AbortController();
      abortRefB.current = controller;
      
      setLoadingB(true);
      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        
        // Resolve "default" scenario to actual scenario ID from data scenarios API
        const resolvedScenarioId = await resolveScenarioId(
          rightScenario || 'default',
          rightTemplateKey || '',
          channel || 'Email',
          effectiveProjectId || '',
          fuelType,
          rightLocale || 'en_US',
          (pilot_id, fuel_type, locale, signal) => api.getDataScenarios(pilot_id, fuel_type, locale, { signal }),
          controller.signal
        );
        
        // Backend /v1/cx/render expects: { projectId, scenarioId, uuid?, sectionNames? }
        // sectionNames: empty array for Full Template, or array with technical section name
        // Get sectionNames based on current elementKey (don't use fullHtml for lookup since we're calling API)
        const sectionNamesForApi = getSectionNames(elementKey);
        const resp: DynamicRenderResponse = await api.renderDynamic({
          projectId: effectiveProjectId || '',
          scenarioId: resolvedScenarioId,
          uuid: '', // UUID is optional, backend will get it from project
          sectionNames: sectionNamesForApi
        }, { signal: controller.signal });
        if (active && !controller.signal.aborted) {
          setHtmlB(resp.html || '');
          setSubjectB(resp.subject || ''); // renderDynamic returns subject
        }
      } catch (err) {
        const anyErr: any = err;
        const isAborted =
          anyErr?.code === 'ERR_CANCELED' ||
          anyErr?.name === 'CanceledError' ||
          controller.signal.aborted === true;
        if (active && !isAborted) {
          setHtmlB('<div style="padding:12px;color:crimson">Failed to render preview</div>');
          setSubjectB('');
        }
      } finally {
        if (active) setLoadingB(false);
      }
    })();
    return () => {
      active = false;
      abortRefB.current?.abort();
    };
  }, 400, [rightTemplateKey, rightScenario, rightLocale, effectiveProjectId, elementKey, channel, fuelType]);
  if (rightRequest) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr 1px 1fr',
          gap: 2,
          p: 2,
          borderRadius: 2,
          bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
          overflow: 'hidden'
        }}
      >
        <PreviewFrame
          variantName="A"
          variant={variant || request?.variant || null}
          renderRequest={request}
          previewMode={previewMode}
          channel={channel}
          isFullTemplateSelected={isFullTemplateSelected}
          renderedHtml={html}
          renderedSubject={subject}
          loading={loading}
          isWebWidgetTemplate={isWebWidget}
          onWebWidgetFinished={onWebWidgetFinished}
        />
        <Box
          sx={{
            width: '1px',
            bgcolor: 'divider'
          }}
        />
        <PreviewFrame
          variantName="B"
          variant={variant || rightRequest?.variant || null}
          renderRequest={rightRequest}
          previewMode={previewMode}
          channel={channel}
          isFullTemplateSelected={isFullTemplateSelected}
          renderedHtml={htmlB}
          renderedSubject={subjectB}
          loading={loadingB}
          isWebWidgetTemplate={
            channel === 'Web' && isWebWidgetTemplate(rightTemplateKey)
          }
          onWebWidgetFinished={onWebWidgetFinished}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        borderRadius: 2,
        p: 2,
        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        overflow: 'auto'
      }}
    >
      <PreviewFrame
        variant={variant || request?.variant || null}
        renderRequest={request}
        previewMode={previewMode}
        channel={channel}
        isFullTemplateSelected={isFullTemplateSelected}
        renderedHtml={html}
        renderedSubject={subject}
        loading={loading}
        isWebWidgetTemplate={isWebWidget}
        onWebWidgetFinished={onWebWidgetFinished}
      />
    </Box>
  );
};
